import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send, ChevronLeft } from "lucide-react";
import { demoBuddy, demoMessages } from "@/data/tourDemoData";

export const DemoChatPreview = () => {
  const [visibleMessages, setVisibleMessages] = useState<number>(0);

  useEffect(() => {
    // Animate messages appearing one by one
    const timers: ReturnType<typeof setTimeout>[] = [];
    demoMessages.forEach((_, index) => {
      const timer = setTimeout(() => {
        setVisibleMessages(index + 1);
      }, 600 * (index + 1));
      timers.push(timer);
    });

    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <Card className="w-full max-w-sm mx-auto overflow-hidden bg-card animate-scale-in">
      {/* Chat Header */}
      <div className="flex items-center gap-3 p-3 border-b border-border bg-muted/30">
        <Button aria-label="Chat actions" variant="ghost" size="icon" className="h-8 w-8">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <Avatar className="w-9 h-9">
          <AvatarImage src={demoBuddy.avatar} />
          <AvatarFallback className="bg-primary/20 text-primary text-sm">
            {demoBuddy.name.slice(0, 1)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <p className="font-medium text-sm text-foreground">{demoBuddy.name}</p>
          <p className="text-xs text-green-500">Online</p>
        </div>
      </div>

      {/* Messages */}
      <div className="p-3 space-y-3 min-h-[200px] bg-background/50">
        {demoMessages.slice(0, visibleMessages).map((msg) => (
          <div 
            key={msg.id}
            className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
          >
            <div 
              className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                msg.sender === 'user'
                  ? 'bg-primary text-primary-foreground rounded-br-md'
                  : 'bg-muted text-foreground rounded-bl-md'
              }`}
            >
              <p>{msg.content}</p>
              <p className={`text-[10px] mt-1 ${
                msg.sender === 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'
              }`}>
                {msg.time}
              </p>
            </div>
          </div>
        ))}

        {visibleMessages === demoMessages.length && (
          <div className="flex justify-start animate-fade-in">
            <div className="flex items-center gap-1 px-3 py-2 bg-muted rounded-2xl rounded-bl-md">
              <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 p-3 border-t border-border">
        <Input 
          placeholder="Type a message..." 
          className="flex-1 bg-muted/50 border-0"
          readOnly
        />
        <Button aria-label="Send message" size="icon" className="shrink-0">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
};
